import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import React, { FC, useRef, useState } from 'react';
import { Button, Field, Icon } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import { faArrowDownArrowUp } from '@fortawesome/pro-solid-svg-icons/faArrowDownArrowUp';
import TextEditor from 'app/modules/textEditor';
import PageTypeUtils from 'app/utils/pageType';
import { TextEditorFeature } from 'app/modules/textEditor/constants/textEditorFeature';
import { PostComposerFormState } from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import PostComposerContentTextEditor from 'app/modules/posts/modules/post/components/composer/postComposerContent/postComposerContentText/PostComposerContentTextEditor';
import { UseSetStateAction } from 'utils/hooks/useSetState';
import { SUBTYPE } from '@kontentino/kontentino-constants/Posts';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import { Editor } from '@tiptap/core';

type Props = {
  value: PostComposerFormState;
  onChange: UseSetStateAction<PostComposerFormState>;
  onTextEditorInit: (editor: any) => void;
  onTranslationEditorInit: (editor: any) => void;
  swapTextTranslation: () => void;
  disabled?: boolean | string;
  labels?: {
    translation?: string;
    text?: string;
  };
};

const PostComposerContentText: FC<Props> = ({
  value,
  onChange,
  onTextEditorInit,
  onTranslationEditorInit,
  swapTextTranslation,
  disabled,
  labels,
}) => {
  const { t } = useTranslation();
  const textEditor = useRef<Editor | null>(null);
  const autoOpenTranslatePostText = useRef<boolean>(false);
  const { accountPlanType } = useSubscriptionInfo();
  const [isTranslationVisible, setTranslationIsVisible] = useState(
    () => !!value.translationText.length,
  );
  const hideTranslation =
    value.subtype === SUBTYPE.INSTAGRAM_STORY &&
    value.attachments.publishingMethod === 'direct';

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {isTranslationVisible && (
        <>
          <Field.Group>
            <div className="tw-flex tw-items-center">
              <Field.Label required>
                {labels?.translation || t('translation.postTranslation')}
                <span className="tw-font-regular tw-text-grayscale-100">
                  &nbsp;({t('translation.willNotBePostedOn')}{' '}
                  {PageTypeUtils.getLabel(value.page.type)})
                </span>
              </Field.Label>
              {isTranslationVisible && (
                <Button
                  variant="plain"
                  onClick={() => setTranslationIsVisible(false)}
                  size="small"
                  className="tw-ml-auto tw-text-primary-100"
                >
                  {t('translation.hide')}
                </Button>
              )}
            </div>
            <UserPermissionGate
              scopes={UserPermission.POST_EDIT_TRANSLATION}
              deniedProps={{ disabled: true }}
            >
              <TextEditor
                accountPlanType={accountPlanType}
                dataPrefix="posts-form-translation"
                initialValue={value.translationText}
                onDebouncedChange={(translationText) =>
                  onChange({ translationText })
                }
                disabled={disabled}
                page={value.page}
                onInit={(editor) => {
                  onTranslationEditorInit(editor);
                  if (
                    autoOpenTranslatePostText.current &&
                    !value.translationText
                  ) {
                    editor.commands.setContent(
                      textEditor.current?.getHTML() || '',
                      false,
                      {
                        preserveWhitespace: true,
                      },
                    );
                    autoOpenTranslatePostText.current = false;
                  }
                }}
                optionsBarConfig={{
                  popover: {
                    initialOpen: autoOpenTranslatePostText.current
                      ? TextEditorFeature.Translation
                      : undefined,
                  },
                }}
                features={[
                  TextEditorFeature.UndoRedo,
                  TextEditorFeature.EmojiPicker,
                  TextEditorFeature.PageMention,
                  TextEditorFeature.DirectMention,
                  TextEditorFeature.HashtagsPresets,
                  TextEditorFeature.LinkShortener,
                  TextEditorFeature.Translation,
                ]}
              />
            </UserPermissionGate>
          </Field.Group>

          <UserPermissionGate scopes={UserPermission.POST_EDIT_TEXT}>
            <Field.Group>
              <Button
                variant="tertiary"
                iconBefore={<Icon icon={faArrowDownArrowUp} />}
                onClick={swapTextTranslation}
                disabled={hideTranslation}
              >
                {t('translation.swapText')}
              </Button>
            </Field.Group>
          </UserPermissionGate>
        </>
      )}
      <Field.Group>
        <div className="tw-flex tw-items-center">
          <Field.Label required>
            {labels?.text || t('translation.postText')}
          </Field.Label>
          {!isTranslationVisible && (
            <>
              {!value.translationText && (
                <UserPermissionGate scopes={UserPermission.POST_EDIT_TEXT}>
                  <Button
                    onClick={() => {
                      setTranslationIsVisible(true);

                      if (!value.translationText) {
                        autoOpenTranslatePostText.current = true;
                      }
                    }}
                    variant="plain"
                    size="small"
                    className="tw-ml-auto tw-text-primary-100"
                    data-cy="post-composer-translation-button"
                  >
                    {t('translation.add')}
                  </Button>
                </UserPermissionGate>
              )}
              {!!value.translationText && (
                <Button
                  onClick={() => setTranslationIsVisible(true)}
                  variant="plain"
                  size="small"
                  className="tw-ml-auto tw-text-primary-100"
                  data-cy="post-composer-translation-button"
                >
                  {t('translation.show')}
                </Button>
              )}
            </>
          )}
        </div>
        <UserPermissionGate
          scopes={UserPermission.POST_EDIT_TEXT}
          deniedProps={{ disabled: true }}
        >
          <PostComposerContentTextEditor
            onInit={(editor) => {
              onTextEditorInit(editor);
              textEditor.current = editor;
            }}
            onChange={onChange}
            disabled={disabled}
            value={{
              text: value.text,
              page: value.page,
              audienceRestrictions: value.audienceRestrictions,
            }}
          />
        </UserPermissionGate>
      </Field.Group>
    </div>
  );
};

export default PostComposerContentText;
